// @owners { team: patient-health }
import { createSelector } from 'reselect';
import { getMedicationsFromPrescriptions, getSelectedMedication } from './getMedicationsFromPrescriptions';
import { ACTIONABLE_PRESCRIPTION_STATES } from '~shared/features/my-meds/constants';
import { sortMultipleActivePrescriptions } from '~shared/features/my-meds/helpers/sortPrescriptions';
import { isActiveRx } from '~shared/features/prescriptions/helper';
import { getAvailabilityDataByPrescriptionID__DEPRECATED } from '~shared/features/prescriptions/selectors/getAvailabilityDataByPrescriptionID';
import { type MedicationParam } from '~shared/selectors/selectorParamTypes';
import { type ReduxStateShared } from '~shared/types';
import { type Prescription } from '~shared/types/clients';

type PartitionedPrescriptions = {
  actionablePrescriptions: Prescription[];
  inactivePrescriptions: Prescription[];
};

type PartitionedPrescriptionsByMedication = Record<string, PartitionedPrescriptions>;

const partitionPrescriptionsByActiveState = (prescriptions: Prescription[]) => {
  const actionablePrescriptions: Prescription[] = [];
  const inactivePrescriptions: Prescription[] = [];

  prescriptions.forEach((prescription) => {
    const { active_state } = prescription;
    if (active_state && ACTIONABLE_PRESCRIPTION_STATES.includes(active_state)) {
      actionablePrescriptions.push(prescription);
    } else {
      inactivePrescriptions.push(prescription);
    }
  });

  const partitionedPrescriptions: PartitionedPrescriptions = {
    actionablePrescriptions,
    inactivePrescriptions,
  };
  return partitionedPrescriptions;
};

const getPartitionedPrescriptionsByMedication = createSelector(
  [getMedicationsFromPrescriptions, getAvailabilityDataByPrescriptionID__DEPRECATED],
  (medications, availabilityDataByPrescriptionID) => {
    const partitionedPrescriptionsByMedication: PartitionedPrescriptionsByMedication = {};

    medications.forEach((medication) => {
      if (medication.key) {
        const sortedPrescriptions = sortMultipleActivePrescriptions(
          medication.prescriptions,
          availabilityDataByPrescriptionID,
        );
        partitionedPrescriptionsByMedication[medication.key] = partitionPrescriptionsByActiveState(sortedPrescriptions);
      }
    });

    return partitionedPrescriptionsByMedication;
  },
);

export const getPartitionedPrescriptionsForSelectedMedication = createSelector(
  getPartitionedPrescriptionsByMedication,
  getSelectedMedication,
  (partitionedPrescriptionsByMedication, medication) => {
    if (!medication?.key) {
      return {
        actionablePrescriptions: [],
        inactivePrescriptions: [],
      };
    }

    return partitionedPrescriptionsByMedication[medication.key];
  },
);

const getMedication = (_: ReduxStateShared, props: MedicationParam) => props.medication;

export const getPartitionedPrescriptionsForMedication = createSelector(
  getPartitionedPrescriptionsByMedication,
  getMedication,
  (partitionedPrescriptionsByMedication, medication) => {
    if (!medication?.key) {
      return {
        actionablePrescriptions: [],
        inactivePrescriptions: [],
      };
    }

    return partitionedPrescriptionsByMedication[medication.key];
  },
);

export const getActiveActionablePrescriptions = createSelector(
  getPartitionedPrescriptionsForMedication,
  ({ actionablePrescriptions }) => {
    return actionablePrescriptions.filter(isActiveRx);
  },
);
